import { useDispatch, useSelector } from "react-redux";
import { platformActions, RootStatePlatform } from "../../store/platform-slice";
import useTheme from "../../../../common/hooks/useTheme";

import React, { useEffect, useMemo, useRef, useState } from "react";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { Theme } from "../../../../providers/ThemeProvider";
import { systemGroupType } from "../../../../providers/dataPlatformReportTypes";
import { getCategoryColor } from "../../store/helper";
import {
  isOppositeGenderSystem,
  useClientGender,
} from "../../hooks/useClientGender";
import { useScoreSidePanel } from "./ScoreSidePanelContext";
import { useAppStore } from "../../../../providers/AppStore";
import { useSubject } from "../../providers/SubjectProvider";
import {
  ENERGY_TRAINING_SYSTEM_ID,
  ENERGY_TRAINING_SYSTEM_NAME,
  hasTrainingTableData,
} from "../healthOverview/energyTraining/energyTrainingData";
import { hasZoneData } from "../../../consultation/data/energyTrainingZones";

// How the score badges render while the report is a sample (isDemo):
// "faded" keeps the sample scores visible at half opacity as a preview;
// "hidden" drops them so the rail is pure navigation (used while the
// All-biomarkers view — real, editable data — is open). Real reports are
// unaffected by this prop.
type DemoBadgeMode = "faded" | "hidden";

const ScoreScreenSidebar = ({
  demoBadgeMode = "faded",
}: {
  demoBadgeMode?: DemoBadgeMode;
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );
  const gender = useClientGender();
  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );

  // The rail renders exactly what the report scored — a card needs a score,
  // and only `score.systems[]` has one. When Body Composition Analysis (26) is
  // missing here while the client has InBody readings, the report is the thing
  // to fix: see claude_docs/BUG_BODY_COMPOSITION_MISSING.md.
  const systemCards = useMemo(
    () =>
      (reportData?.systems ?? []).filter((item) => {
        // Sexual-wellness gender filter: a male client sees system 11 and not
        // 12, a female client the opposite, and an unknown gender hides
        // neither.
        if (isOppositeGenderSystem(item.system, gender)) return false;
        // System 26 is the Body Composition entry the backend appends to
        // `score.systems[]`. Only show it when it actually carries biomarkers
        // — otherwise there's nothing to display in the detail panel.
        if (item.system === 26 && (item.bloodBiomarkers?.length ?? 0) === 0)
          return false;
        return true;
      }),
    [reportData, gender]
  );

  // Energy & Training Zones has no row in `score.systems[]`: it is listed
  // last, like in the consultation, and only when the client has zone data —
  // either the consultation zones (graph) or the report's training table.
  const workoutPlan = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer?.programming?.workoutPlan
  );
  const { subjectId } = useSubject();
  const { ASConsultation } = useAppStore();
  const consultation = ASConsultation(subjectId);
  const showEnergyTraining =
    hasTrainingTableData(workoutPlan) ||
    hasZoneData(consultation?.energyTraining?.zones);

  // Keep the selected system's card in view — selections also arrive from
  // outside the rail (the All-biomarkers System dropdown). `nearest` scrolls
  // the minimum needed, so clicks on already-visible cards don't move
  // anything.
  useEffect(() => {
    if (selectedSystem === 0 || !containerRef.current) return;
    containerRef.current
      .querySelector("[data-selected='true']")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedSystem]);

  return (
    <div ref={containerRef} style={styles.container(theme)}>
      <ScrollableWrapper customPadding={"0"}>
        {/* `score` is briefly undefined right after a mutation clears
            dataPlatformServer (e.g. saving the Review health scores modal),
            until the report refetch lands. */}
        {systemCards.map((item, index: number) => (
          <SystemCard data={item} demoBadgeMode={demoBadgeMode} key={index} />
        ))}
        {/* No score — a presentation of the zones, not a scored system. */}
        {showEnergyTraining && (
          <RailCard
            systemId={ENERGY_TRAINING_SYSTEM_ID}
            name={ENERGY_TRAINING_SYSTEM_NAME}
          />
        )}
      </ScrollableWrapper>
    </div>
  );
};

const SystemCard = ({
  data,
  demoBadgeMode,
}: {
  data: systemGroupType;
  demoBadgeMode: DemoBadgeMode;
}) => {
  const theme = useTheme();
  let categoryColor = getCategoryColor(
    "%",
    data.score,
    theme,
    data.scoreCategory
  );

  const isDemoReport = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.isDemo
  );

  const showBadge =
    data.approval === 4 && !(isDemoReport && demoBadgeMode === "hidden");

  return (
    <RailCard systemId={data.system} name={data.name}>
      {showBadge && (
        <div
          style={{
            ...styles.systemCardScore(theme, categoryColor),
            // Sample scores render as a half-opacity preview.
            ...(isDemoReport ? { opacity: 0.5 } : {}),
          }}
        >{`${data.score}%`}</div>
      )}
    </RailCard>
  );
};

/** A selectable rail entry; clicking the selected one deselects it. */
const RailCard = ({
  systemId,
  name,
  children,
}: {
  systemId: number;
  name: string;
  children?: React.ReactNode;
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const dispatch = useDispatch();
  const { closePanel } = useScoreSidePanel();

  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );

  return (
    <div data-selected={systemId === selectedSystem ? "true" : undefined}>
      <div
        style={styles.systemCard(theme, systemId === selectedSystem, isHovered)}
        onClick={() => {
          if (selectedSystem === systemId) {
            dispatch(platformActions.setSelectedSystem(0));
            dispatch(platformActions.setSelectedActiveConnection(0));
            closePanel();
          } else {
            dispatch(platformActions.setSelectedSystem(systemId));
            dispatch(platformActions.setSelectedActiveConnection(0));
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.systemCardTitle(theme)}>{name}</div>
        {children}
      </div>
    </div>
  );
};

export default ScoreScreenSidebar;

const styles = {
  container: (theme: Theme) => ({
    width: "265px",
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
  }),
  systemCard: (theme: Theme, selected: boolean, hovered: boolean) => ({
    padding: "8px 12px",
    minHeight: "48px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${
      selected
        ? theme.colors_ds.PRIMARY_TEXT_MAIN
        : hovered
        ? theme.colors_ds.NEUTRAL_BORDER_DEFAULT
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    boxShadow: selected ? "var(--box-shadow-general)" : "none",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }),
  systemCardTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  systemCardScore: (theme: Theme, categoryColor: string) => ({
    width: "46px",
    textAlign: "center" as "center",
    padding: "4px",
    borderRadius: "4px",
    backgroundColor: theme.colorTokens.N[30],
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    color: categoryColor,
  }),
};
